import { V6Client } from "@aws-amplify/api-graphql";
import { differenceInCalendarDays, format, isValid, parse } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import parseAddress from "parse-address";
import type {
  AudioByUploadedFileIDQueryVariables,
  DocumentsByUploadedFileIDQueryVariables,
} from "~/api/API";
import {
  AppointmentRecord,
  AppointmentRecordPatientCase,
  AppointmentStatus,
  AppointmentsView,
  AppointmentType,
  BodyPart,
  Doctor,
  FileProvider,
  FileType,
  InterpreterInfo,
  Language,
  Location,
  LocationStatus,
  ParticipantEmailStatuses,
  ParticipantType,
  RecurrenceType,
  Region,
  ReportStatus,
  StoredAddress,
} from "~/api/API";
import {
  audioByUploadedFileIDWithTranscriptPreview,
  documentsByUploadedFileIDWithPreview,
} from "~/api/graphql-custom/queries";
import { FileDisplayData } from "~/api/queries/useAppointmentFiles";
import {
  AUDIO_FILE_EXTENSIONS,
  RECORDS_PREFIX,
  STATE_OPTIONS,
  VISIT_FORM_BASE_URL,
  VISIT_FORM_BASE_URL_SPANISH,
} from "~/constants";
import { amplifyGraphQLClient } from "~/lib/amplify-client";
import { AttorneyInfo } from "~/models";
import {
  convertISOToLocalDateObject,
  extractYearMonthFromDateISO,
  formatDate,
} from "./converters";
import { DateISO, ISO_DATE_REGEX } from "./DateISO";
import { sortPatientCases } from "./sortings";
import type { MedicalFile } from "./types";

const PACIFIC_TIMEZONE = "America/Los_Angeles";

export const joinPaths = (...paths: string[]): string => {
  return paths.map((path) => path.replace(/^\/|\/$/g, "")).join("/");
};

export const generateTimestamp = (): string => {
  return new Date().toISOString().replace(/[-:]/g, "").replace(/\..+/, "");
};

export const normalizeNullableId = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : null;
};

/**
 * Drop keys whose values are ``null`` or ``undefined``. Top-level only —
 * not recursive.
 *
 * Use case: building AppSync ``create*`` mutation inputs. The Amplify
 * Gen 1 VTL resolvers strip nulls only for a hardcoded ``indexFields``
 * allowlist (typically just the GSI keys); every other ``null`` gets
 * serialized into DynamoDB as the typed-NULL marker ``{NULL: true}``.
 * That's not the same as the attribute being absent:
 *   - ``attribute_exists()`` returns true for ``{NULL: true}``.
 *   - GSIs with non-NULL types reject the row once they're ACTIVE.
 *   - Downstream code that distinguishes "is this field set" gets the
 *     wrong answer.
 *
 * The 672-row ``reportDue`` pollution incident (Apr 2026, via
 * ``appointment.ts:createRescheduledFromExistingAppointment``) traced
 * back to passing through a null field on a create-mutation input. The
 * Python side has a recursive equivalent at
 * ``services.graphql_client.null_stripping.drop_nulls``; this is the
 * portal-side counterpart.
 *
 * **Only safe for CREATE mutations.** For UPDATE mutations, ``null`` may
 * legitimately mean "clear this column" — handle case-by-case.
 *
 * Top-level only is sufficient for AppSync inputs in practice — nested
 * ``@model`` types aren't passed as embedded objects; they go through
 * separate mutations. If you find yourself needing recursion, port the
 * Python helper instead.
 */
export const omitNullish = <T extends Record<string, unknown>>(input: T): T =>
  Object.fromEntries(
    Object.entries(input).filter(([, value]) => value != null),
  ) as T;

/**
 * Checks whether a given appointment status is part of any hold lifecycle
 * (rescheduling or deposition). Consumers use this to surface status-specific
 * notes when structured reason/requester fields may be absent.
 *
 * Covered statuses: RESCHEDULING_IN_PROGRESS, RESCHEDULING_HOLD,
 * LATE_RESCHEDULING_HOLD, DEPOSITION_HOLD, REEVAL_HOLD.
 */
export const isHoldLifecycleStatus = (
  status: AppointmentStatus | null | undefined,
): boolean =>
  status === AppointmentStatus.RESCHEDULING_IN_PROGRESS ||
  status === AppointmentStatus.RESCHEDULING_HOLD ||
  status === AppointmentStatus.LATE_RESCHEDULING_HOLD ||
  status === AppointmentStatus.DEPOSITION_HOLD ||
  status === AppointmentStatus.REEVAL_HOLD;

export const capitalize = (str: string): string =>
  str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const isAudioFile = (fileName: string): boolean => {
  const lowerCaseFileName = fileName.toLowerCase();
  return AUDIO_FILE_EXTENSIONS.some((ext) => lowerCaseFileName.endsWith(ext));
};

const filteredPatientCases = (appointment: AppointmentRecord) => {
  if (appointment?.patientCases?.items) {
    return sortPatientCases(appointment);
  }
  return [];
};

export const isCaseFinishedUpcoming = (appointment: AppointmentRecord) => {
  const confirmationEmailStatuses =
    appointment?.caseInfo?.confirmationEmailStatuses;

  if (!confirmationEmailStatuses) return false;

  let isFinished =
    confirmationEmailStatuses.patient && confirmationEmailStatuses.adjuster;

  const patientCases = filteredPatientCases(appointment);
  if (patientCases.length === 0) return false;

  const primaryPatientCase = patientCases[0]?.patientCase;
  if (!primaryPatientCase) return false;

  if (
    isAttorneyPresent(
      primaryPatientCase.applicantAttorney,
      primaryPatientCase.applicantAttorneyLawFirm?.name,
      primaryPatientCase.applicantAttorneyAddress,
    )
  ) {
    isFinished = isFinished && confirmationEmailStatuses.applicantAttorney;
  }

  if (
    isAttorneyPresent(
      primaryPatientCase.defenseAttorney,
      primaryPatientCase.defenseAttorneyLawFirm?.name,
      primaryPatientCase.defenseAttorneyAddress,
    )
  ) {
    isFinished = isFinished && confirmationEmailStatuses.defenseAttorney;
  }

  return isFinished;
};

/**
 * Checks whether an attorney is present based on attorney info, law firm name, and address.
 */
export const isAttorneyPresent = (
  attorney: AttorneyInfo | null | undefined,
  lawFirmName: string | null | undefined,
  lawFirmAddress: StoredAddress | null | undefined,
): boolean => {
  const hasAttorneyInfo = !!attorney?.name;
  const hasName = !!lawFirmName?.trim();
  const hasAddress = !!(
    lawFirmAddress?.street?.trim() ||
    lawFirmAddress?.city?.trim() ||
    lawFirmAddress?.state?.trim() ||
    lawFirmAddress?.zip?.trim()
  );

  return hasAttorneyInfo || hasName || hasAddress;
};

type EmailStatusKeys = keyof ParticipantEmailStatuses;

export type ParticipantEmailStatusKey = Extract<
  EmailStatusKeys,
  "patient" | "adjuster" | "applicantAttorney" | "defenseAttorney"
>;

export const participantToEmailStatusKey: Record<
  ParticipantType,
  ParticipantEmailStatusKey
> = {
  [ParticipantType.PATIENT]: "patient",
  [ParticipantType.ADJUSTER]: "adjuster",
  [ParticipantType.APPLICANT_ATTORNEY]: "applicantAttorney",
  [ParticipantType.DEFENSE_ATTORNEY]: "defenseAttorney",
};

export type ParticipantSentAtKey = Extract<
  EmailStatusKeys,
  `${ParticipantEmailStatusKey}SentAt`
>;

export const participantSentAtKeyMap: Record<
  ParticipantEmailStatusKey,
  ParticipantSentAtKey
> = {
  patient: "patientSentAt",
  adjuster: "adjusterSentAt",
  applicantAttorney: "applicantAttorneySentAt",
  defenseAttorney: "defenseAttorneySentAt",
};

export const getParticipantSentAtKey = (
  field: ParticipantEmailStatusKey,
): ParticipantSentAtKey => participantSentAtKeyMap[field];

export const isFutureAppointmentDate = (
  appointmentDate?: string | null,
): boolean => {
  if (!appointmentDate) {
    return false;
  }

  const parsedDate = DateISO.fromString(appointmentDate);
  if (!parsedDate) {
    return false;
  }

  const todayIso = getTodayDateISOStr();
  return parsedDate.compareTo(todayIso) >= 0;
};

export const isNoInterpreterNeeded = (language: Language): boolean => {
  return language === Language.ENGLISH;
};

/**
 * Legacy interpreter "confirmed" heuristic: any interpreter contact field is
 * filled. Used when the INTERPRETER_CONFIRMATION flag is off. Prefer
 * isInterpreterExplicitlyConfirmed once the flag is GA'd.
 */
export const isInterpreterConfirmed = (
  interpreterInfo?: InterpreterInfo | null,
): boolean => {
  return (
    !!interpreterInfo?.companyName ||
    !!interpreterInfo?.phoneNumber ||
    !!interpreterInfo?.phoneExtension ||
    !!interpreterInfo?.note
  );
};

/**
 * Explicit interpreter confirmation (INTERPRETER_CONFIRMATION flow): true only
 * when ops manually marked the interpreter as confirmed.
 */
export const isInterpreterExplicitlyConfirmed = (
  interpreterInfo?: InterpreterInfo | null,
): boolean => {
  return interpreterInfo?.confirmed === true;
};

export const isEmailDraftingEligibleAppointmentType = (
  appointmentType: AppointmentType,
): boolean => {
  return [
    AppointmentType.QME,
    AppointmentType.QME_REEVALUATION,
    AppointmentType.AME,
    AppointmentType.AME_REEVALUATION,
    AppointmentType.SIBTF,
    AppointmentType.SIBTF_REEVALUATION,
    AppointmentType.IME,
    AppointmentType.IME_REEVALUATION,
    AppointmentType.IME_ADR,
    AppointmentType.IME_ADR_REEVALUATION,
  ].includes(appointmentType);
};

// Attendance email is only meaningful when the appointment took place
// (still in SCHEDULED) or the patient/interpreter no-showed. Reschedules,
// cancellations, holds, and inquiries should not draft an attendance email.
export const isAttendanceEmailEligibleStatus = (
  status: AppointmentStatus | null | undefined,
): boolean =>
  status === AppointmentStatus.SCHEDULED ||
  status === AppointmentStatus.NO_SHOW;

/**
 * Determines if files include medical records or cover letters from DA or ADJ
 *
 * @param files - Files from useAppointmentFiles
 * @param currentAppointmentOnly - If true, only consider non-inherited files
 * @param includeRelevantCoverLetters - If true, also check for DA/ADJ cover letters
 */
export const hasMedicalRecordFiles = (
  files: FileDisplayData[],
  currentAppointmentOnly = false,
  includeRelevantCoverLetters = true,
): boolean => {
  if (files.length === 0) {
    return false;
  }

  const filteredFiles = currentAppointmentOnly
    ? files.filter((file) => !file.isReadOnly)
    : files;

  return filteredFiles.some((file) => {
    if (file.fileType === FileType.MEDICAL_RECORD) {
      return true;
    }

    if (
      includeRelevantCoverLetters &&
      file.fileType === FileType.COVER_LETTER
    ) {
      if (
        file.providedBy === FileProvider.DEFENSE_ATTORNEY ||
        file.providedBy === FileProvider.ADJUSTER
      ) {
        return true;
      }
    }

    return false;
  });
};

export type RemoveTypename<T> =
  T extends Array<infer U>
    ? Array<RemoveTypename<U>>
    : T extends object
      ? { [K in Exclude<keyof T, "__typename">]: RemoveTypename<T[K]> }
      : T;

export function removeTypenames<T>(input: T): RemoveTypename<T> {
  if (Array.isArray(input)) {
    return (input as unknown[]).map((item) =>
      removeTypenames(item),
    ) as RemoveTypename<T>;
  }
  if (typeof input === "object" && input !== null) {
    const { __typename, ...rest } = input as Record<string, unknown>;
    const cleanRest = Object.keys(rest).reduce(
      (acc: Record<string, unknown>, key) => {
        acc[key] = removeTypenames(rest[key]);
        return acc;
      },
      {},
    );
    return cleanRest as RemoveTypename<T>;
  }
  return input as RemoveTypename<T>;
}

/**
 * Pauses execution for the specified number of milliseconds.
 * @param ms - The number of milliseconds to wait.
 * @returns A Promise that resolves after the specified delay.
 */
export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const generateAppointmentS3FileKey = (
  firstClaimNumber: string,
  appointmentDateISO: string,
) => {
  return joinPaths(
    RECORDS_PREFIX,
    firstClaimNumber,
    appointmentDateISO,
    generateTimestamp(),
  );
};

export const getTodayDateISOStr = () => {
  return new Date().toLocaleDateString("en-CA", {
    timeZone: "America/Los_Angeles",
  });
};

export const getTodayDateISO = (): DateISO => {
  const todayDateISO = DateISO.fromString(getTodayDateISOStr());
  // TODO: enforce not null typing in DateISO.fromString
  if (!todayDateISO) {
    throw new Error("Failed to create DateISO from today's date");
  }
  return todayDateISO;
};

export const getFutureDateISO = (days: number) => {
  const futureDate = new Date();
  futureDate.setDate(futureDate.getDate() + days);
  return futureDate.toLocaleDateString("en-CA", {
    timeZone: "America/Los_Angeles",
  });
};

export const getFutureDateFromDateISO = (dateISO: string, days: number) => {
  // Parse the date components directly to avoid timezone issues
  // when we just need to add/subtract days from a date string
  const [year, month, day] = dateISO.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return date.toISOString().split("T")[0];
};

// ISO date (PT) exactly `years` years before today. Used to default list views
// to a recent window so the initial chronological query stays bounded/fast.
export const getPastYearDateISOStr = (years = 1): string => {
  const [year, month, day] = getTodayDateISOStr().split("-").map(Number);
  const date = new Date(Date.UTC(year - years, month - 1, day));
  return date.toISOString().split("T")[0];
};

export const getTodayDateMonth = () => {
  const todayDateISO = getTodayDateISOStr();
  return todayDateISO.split("-")[1];
};

export const getTodayDateDay = () => {
  const todayDateISO = getTodayDateISOStr();
  return todayDateISO.split("-")[2];
};

export const getTodayDateObject = () => {
  const todayDateISO = getTodayDateISOStr();
  return new Date(todayDateISO);
};

export const isNaString = (value: string | null | undefined) => {
  return value === "N/A" || value === "NA" || value === "n/a" || value === "-";
};

export const dateToISO = (date: Date) => {
  return date.toISOString().split("T")[0];
};

/**
 * Parses a date string (assumed to be in 'YYYY-MM-DD' format representing
 * a date in the Pacific Time Zone) and returns a JavaScript Date object
 * representing the beginning of that day (midnight) in Pacific Time.
 *
 * Requires `date-fns-tz` to be installed.
 *
 * Example:
 * getPSTDateFromDateISO('2023-10-27') will return a Date object
 * representing the UTC instant corresponding to 2023-10-27 00:00:00 in 'America/Los_Angeles'.
 *
 * @param dateString - The date string in 'YYYY-MM-DD' format.
 * @returns A Date object representing midnight PST on the given date.
 */
export const getPSTDateFromDateISO = (dateString: string): Date => {
  // fromZonedTime interprets 'YYYY-MM-DD' as midnight in the specified timezone
  // and returns the corresponding Date object (which is internally UTC).
  try {
    return fromZonedTime(dateString, PACIFIC_TIMEZONE);
  } catch (error) {
    console.error(
      `Error parsing date string "${dateString}" with timezone ${PACIFIC_TIMEZONE}:`,
      error,
    );
    // Fallback or re-throw error depending on desired behavior
    return new Date(NaN); // Return an invalid date
  }
};

/**
 * Returns the number of whole calendar weeks from `anchor` to `target`,
 * with both endpoints evaluated on the **Pacific** calendar.
 *
 * Use this for cadence math where the schedule is authored in Pacific
 * time (doctor availability, "every other Tuesday", etc.) and must be
 * stable regardless of the viewer's browser timezone. A naive
 * millisecond-based diff (`dayjs(a).diff(b, "week")`) silently slips the
 * cycle by one week for any non-PST viewer because the 3–4h gap between
 * PST midnight and the viewer's local midnight rounds down through the
 * 7-day boundary in `Math.trunc`. See GET-XXXX (Schedule Assistant
 * bi-weekly bug affecting East Coast medical assistants).
 *
 * `anchor` is treated as-is — intended for Dates already positioned at
 * Pacific midnight (e.g. one returned by `getPSTDateFromDateISO`).
 *
 * `target` may carry any wall-clock time. Its **local (browser)** calendar
 * day is read and re-anchored to Pacific midnight, so a Date built via
 * local-time operations (`setHours(0,0,0,0)` / `addDays`) still produces
 * the correct PST-calendar week index for every viewer.
 */
export const pstCalendarWeeksFromAnchor = (
  anchor: Date,
  target: Date,
): number => {
  const y = target.getFullYear();
  const m = String(target.getMonth() + 1).padStart(2, "0");
  const d = String(target.getDate()).padStart(2, "0");
  const targetAsPstMidnight = getPSTDateFromDateISO(`${y}-${m}-${d}`);
  return Math.floor(differenceInCalendarDays(targetAsPstMidnight, anchor) / 7);
};

/**
 * Gets the current date and time, adjusted to the Pacific Time Zone (America/Los_Angeles),
 * correctly handling Daylight Saving Time.
 *
 * Requires `date-fns-tz` to be installed.
 *
 * @returns A Date object representing the current moment in Pacific Time.
 */
export const getCurrentPSTDate = (): Date => {
  // Get the current UTC time
  const now = new Date();
  // Convert it to a Date object representing the equivalent wall time in PST/PDT
  return toZonedTime(now, PACIFIC_TIMEZONE);
};

// Safely parses a string to a number.
// Returns null if parsing fails or results in NaN.
export const getNumber = (value: string): number | null => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Returns the absolute number of days between two ISO date strings (YYYY-MM-DD).
 * Uses UTC to avoid timezone issues.
 */
export const daysBetweenISODates = (dateA: string, dateB: string): number =>
  Math.abs(signedDaysBetweenISODates(dateA, dateB));

/**
 * Returns the signed number of days from dateA to dateB (YYYY-MM-DD).
 * Positive when dateB is in the future relative to dateA.
 * Uses UTC to avoid timezone issues.
 */
export const signedDaysBetweenISODates = (
  dateA: string,
  dateB: string,
): number => {
  const [aY, aM, aD] = dateA.split("-").map(Number);
  const [bY, bM, bD] = dateB.split("-").map(Number);
  return Math.round(
    (Date.UTC(bY, bM - 1, bD) - Date.UTC(aY, aM - 1, aD)) /
      (1000 * 60 * 60 * 24),
  );
};

export const generateDoctorYearMonths = (
  doctorId: string | null | undefined,
  dateISO: string,
): string => {
  const yearMonth = extractYearMonthFromDateISO(dateISO);
  return doctorId ? `${doctorId}#${yearMonth}` : `-#${yearMonth}`;
};

export const getPrimaryPatientCase = (
  appointment: AppointmentRecord,
): AppointmentRecordPatientCase | undefined => {
  // Filter out null cases first
  const validPatientCases =
    appointment.patientCases?.items.filter(
      (pc): pc is NonNullable<typeof pc> => pc !== null,
    ) || [];

  // Get non-primary cases while preserving order
  // First, find the primary case in the items array
  const primaryCase = validPatientCases.find(
    (pc) => pc.patientCase?.id === appointment.primaryPatientCaseID,
  );

  // If no primary case found with primaryPatientCaseID, use the first case as primary
  return primaryCase || validPatientCases[0];
};

/**
 * Extracts the primary case and other cases from an appointment's patient cases.
 * If no primary case is found with primaryPatientCaseID, uses the first case as primary.
 * @param appointment - The appointment record containing patient cases
 * @returns An object containing the primary case, primary case ID and other cases
 */
export const extractPrimaryCaseAndOthers = (appointment: AppointmentRecord) => {
  // Filter out null cases first
  const validPatientCases =
    appointment.patientCases?.items.filter(
      (pc): pc is NonNullable<typeof pc> => pc !== null,
    ) || [];

  const primaryCase = getPrimaryPatientCase(appointment);
  const primaryCaseId = primaryCase?.patientCase?.id;

  // Get non-primary cases
  const otherCases = validPatientCases.filter(
    (pc) => pc.patientCase?.id !== primaryCaseId,
  );

  // Safety check: Verify we haven't lost any cases in the process
  if (otherCases.length + 1 !== validPatientCases.length) {
    console.error("Case count mismatch during processing", {
      total: validPatientCases.length,
      primary: 1,
      others: otherCases.length,
    });
    return {
      primaryCase: null,
      effectivePrimaryCaseId: null,
      otherCases: [],
    };
  }

  return {
    primaryCase,
    effectivePrimaryCaseId: primaryCaseId,
    otherCases,
  };
};

export const isValidISODate = (date: string): boolean => {
  return /^\d{4}-\d{2}-\d{2}$/.test(date);
};

export const isValidHHMM = (time: string): boolean => {
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);
};

/**
 * Open a URL in a new, isolated browser tab.
 *
 * Passing "noopener,noreferrer" severs the script connection to the opened
 * tab: it receives no `window.opener`, and the browser places it in a fresh
 * browsing-context group — which means its own renderer process and heap
 * budget. A plain `window.open(url, "_blank")` instead keeps the new tab in the
 * opener's process, so every case/preview tab shares one ~4 GB JS heap; when
 * any one tab exhausts it, the shared process dies and every Indigo tab blanks
 * at once. `noopener` also closes the reverse-tabnabbing hole (`window.opener`).
 *
 * Note: with "noopener" `window.open` returns null by design, so callers can
 * neither drive the new tab nor detect a blocked pop-up. Every use here is a
 * user-initiated click (rarely pop-up-blocked). If you genuinely need the
 * window handle, call `window.open` directly and document why the opener link
 * must stay.
 */
export const openInNewTab = (url: string): void => {
  window.open(url, "_blank", "noopener,noreferrer");
};

// Returns the current environment (dev, stage, prod)
export const getCurrentEnv = (): "dev" | "stage" | "prod" => {
  // TODO (Sompop): fix process.env undefined in Ladle
  // !!!IMPORTANT!!! environment variables get replaced by the actual values during build time
  // do not try to do conditional logic based on `process.env` object itself
  const currentEnv = process.env.REACT_APP_ENV || "dev";
  // raise an error if the environment is not one of the expected values
  if (!["dev", "stage", "prod"].includes(currentEnv)) {
    throw new Error(
      `Invalid environment "${currentEnv}". Use one of: dev, stage, prod`,
    );
  }
  return currentEnv as "dev" | "stage" | "prod";
};

export const getSentryDsn = (): string => {
  return process.env.REACT_APP_SENTRY_DSN || "";
};

// Returns the current release from the environment variables
// Format: "portal@<commit-hash>"
export const getSentryRelease = (): string => {
  return process.env.REACT_APP_SENTRY_RELEASE || "";
};

/**
 * Parses a string date into a DateISO object using date-fns for better validation and format support.
 * Supports: ISO (YYYY-MM-DD), MM/DD/YYYY, MM/DD/YY, MMM D YYYY, MMMM D YYYY
 * @param dateStr - The date string to parse.
 * @returns A DateISO object if the date string is valid, otherwise null.
 */
export const parseStringDate = (dateStr: string | null): DateISO | null => {
  if (!dateStr || typeof dateStr !== "string") return null;

  const trimmed = dateStr.trim();
  if (!trimmed) return null;

  // If it's already in ISO format, use it directly (fastest path)
  if (ISO_DATE_REGEX.test(trimmed)) {
    return DateISO.fromString(trimmed);
  }

  // Reference date for parsing (required by date-fns parse function)
  const referenceDate = new Date();

  // Try common date formats in order of likelihood
  const formats = [
    "MM/dd/yyyy", // 01/15/2024
    "MM/dd/yy", // 01/15/24
    "M/d/yyyy", // 1/5/2024
    "M/d/yy", // 1/5/24
    "MMM d, yyyy", // Jan 15, 2024
    "MMM d yyyy", // Jan 15 2024 (no comma)
    "MMMM d, yyyy", // January 15, 2024
    "MMMM d yyyy", // January 15 2024 (no comma)
  ];

  for (const formatStr of formats) {
    try {
      const parsed = parse(trimmed, formatStr, referenceDate);
      if (isValid(parsed)) {
        // Convert to ISO format string (YYYY-MM-DD)
        const isoString = format(parsed, "yyyy-MM-dd");
        return DateISO.fromString(isoString);
      }
    } catch {
      // Continue to next format
    }
  }

  return null;
};

export const extractExaminationBodyParts = (
  claimInfoWithInjuredBodyParts: { injuredBodyParts: BodyPart[] | null }[],
): BodyPart[] => {
  return claimInfoWithInjuredBodyParts
    .flatMap((claim) => claim.injuredBodyParts)
    .filter(
      (item, index, self): item is BodyPart =>
        item != null && self.indexOf(item) === index,
    );
};

export const extractClaimedBodyParts = (
  claimInfoWithClaimedBodyParts: { claimedBodyParts: BodyPart[] | null }[],
): BodyPart[] => {
  return claimInfoWithClaimedBodyParts
    .flatMap((claim) => claim.claimedBodyParts)
    .filter(
      (item, index, self): item is BodyPart =>
        item != null && self.indexOf(item) === index,
    );
};

export const extractExaminationBodyPartsFromAppointment = (
  appointment: AppointmentRecord,
): BodyPart[] => {
  return (
    appointment.patientCases?.items
      ?.flatMap((item) => item?.patientCase?.injuryInfo?.injuredBodyParts)
      .filter(
        (item, index, self): item is BodyPart =>
          item != null && self.indexOf(item) === index,
      ) || []
  );
};

export const extractClaimedBodyPartsFromAppointment = (
  appointment: AppointmentRecord,
): BodyPart[] => {
  return (
    appointment.patientCases?.items
      ?.flatMap((item) => item?.patientCase?.injuryInfo?.claimedBodyParts)
      .filter(
        (item, index, self): item is BodyPart =>
          item != null && self.indexOf(item) === index,
      ) || []
  );
};

export const calculateAgeFromBirthDate = (dateOfBirth: string): number => {
  const dob = convertISOToLocalDateObject(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDifference = today.getMonth() - dob.getMonth();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < dob.getDate())
  ) {
    age--;
  }
  return age;
};

/**
 * Recursively checks whether all fields in the given object are empty.
 *
 * A field is considered empty if it is:
 * - null
 * - undefined
 * - an empty string
 * - an object whose own fields are all also empty (recursively)
 *
 * This version handles circular references by keeping track of already visited objects.
 *
 * @param obj - The object to check.
 * @param visitedObjects - (For internal use) A set of objects already visited during recursion.
 * @returns true if all fields (including nested) are empty; false otherwise.
 */
export const areAllObjectFieldsEmpty = (
  obj: unknown,
  excludeKeys: string[] = [],
  visitedObjects: Set<object> = new Set(),
): boolean => {
  if (obj === null || obj === undefined || obj === "") {
    return true;
  }

  if (typeof obj !== "object") {
    // For non-object, non-empty values
    return false;
  }
  // If we've already visited this object, return false to prevent infinite recursion
  if (visitedObjects.has(obj)) {
    return false; // Circular reference detected
  }

  // Add current object to the visited set
  visitedObjects.add(obj);

  return Object.keys(obj).every((key) => {
    if (key === "__typename" || excludeKeys.includes(key)) {
      return true; // Skip __typename and explicitly excluded keys
    }

    const value = (obj as Record<string, unknown>)[key];

    if (value === null || value === undefined || value === "") {
      return true;
    }

    if (typeof value === "object" && value !== null) {
      return areAllObjectFieldsEmpty(value, excludeKeys, visitedObjects);
    }

    return false;
  });
};

/**
 * Generates a unique file name by adding a number suffix in parentheses if the file already exists
 * @param fileName The original file name
 * @param existingFiles List of existing file names
 * @returns A unique file name
 */
export const generateUniqueFileName = (
  fileName: string,
  existingFiles: string[],
): string => {
  const dotIndex = fileName.lastIndexOf(".");
  const nameWithoutExtension =
    dotIndex === -1 ? fileName : fileName.substring(0, dotIndex);
  const extension = dotIndex === -1 ? "" : fileName.substring(dotIndex);
  let counter = 1;
  let uniqueName = fileName;

  while (existingFiles.includes(uniqueName)) {
    uniqueName = `${nameWithoutExtension} (${counter})${extension}`;
    counter++;
  }

  return uniqueName;
};

export const getInitials = (fullName: string | null | undefined): string => {
  if (!fullName) return "";
  return fullName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase())
    .join("");
};

export const validateNumber = (value: string) => /^\d*$/.test(value);
export const validateFloatNumber = (value: string) =>
  /^(\d+(\.\d*)?)?$/.test(value);

export interface GroupedFiles {
  files: MedicalFile[];
  date?: string;
  type?: AppointmentType;
  status?: AppointmentStatus;
  appointmentId?: string;
}

/**
 * Defines rules for determining the on-site operations assistant (Op)
 *
 * Each rule maps a physician to one or more locations and assigns an assistant.
 * If no specific rule matches, a default assistant is used.
 *
 * Used by getOnSiteOpName to return the correct assistant based on
 * physician and location.
 */

interface OnSiteOpRule {
  physicianName: string;
  locations: string[];
  assistantName: string;
}

const onSiteOpRules: OnSiteOpRule[] = [
  {
    physicianName: "Leonard Gordon",
    locations: ["all"],
    assistantName: "Letty Soto",
  },
  {
    physicianName: "Joshua Gordon",
    locations: ["Fremont", "Menlo Park", "Santa Clara", "Santa Cruz"],
    assistantName: "Helen Jiang",
  },
  {
    physicianName: "Ephraim Dickinson",
    locations: ["Fremont", "Menlo Park", "Santa Clara", "Santa Cruz"],
    assistantName: "Helen Jiang",
  },
  {
    physicianName: "David G Levinsohn",
    locations: ["Fremont", "Menlo Park", "Santa Clara", "Santa Cruz"],
    assistantName: "Helen Jiang",
  },
  {
    physicianName: "Sho Shimamoto",
    locations: ["Fremont", "Menlo Park", "Santa Clara", "Santa Cruz"],
    assistantName: "Helen Jiang",
  },
];

const defaultOnSiteOpName = "Jason Chen";

export const getOnSiteOpName = (
  physicianName: string,
  locationName: string,
): string => {
  const rule = onSiteOpRules.find(
    (r) =>
      r.physicianName === physicianName &&
      (r.locations.includes(locationName) || r.locations.includes("all")),
  );

  if (rule) {
    return rule.assistantName;
  }

  return defaultOnSiteOpName;
};

const recurrenceLabels: Record<RecurrenceType, string> = {
  [RecurrenceType.WEEKLY]: "Week",
  [RecurrenceType.BIWEEKLY]: "2 weeks",
  [RecurrenceType.QUADWEEKLY]: "4 weeks",
  [RecurrenceType.MONTHLY]: "Month",
};

export const getRecurrenceOptions = (): Array<{
  value: RecurrenceType;
  label: string;
}> => {
  return Object.values(RecurrenceType).map((recurrence: RecurrenceType) => ({
    value: recurrence,
    label: recurrenceLabels[recurrence] ?? capitalize(recurrence),
  }));
};

export const getFullAppointmentUTCDateTime = (a: {
  date?: string | null;
  time?: string | null;
}): Date | null => {
  if (!a.date) return null;

  const time = a.time ?? "00:00";
  const dateTimeStr = `${a.date}T${time}`;

  try {
    return fromZonedTime(dateTimeStr, PACIFIC_TIMEZONE);
  } catch {
    return null;
  }
};

const DUE_DATE_EXCLUDED_STATUSES = new Set([
  AppointmentStatus.INQUIRY,
  AppointmentStatus.INQUIRY_CLOSED,
  AppointmentStatus.CANCELLED,
  AppointmentStatus.LATE_CANCELLED,
  AppointmentStatus.RESCHEDULED,
  AppointmentStatus.LATE_RESCHEDULED,
  AppointmentStatus.RESCHEDULING_HOLD,
  AppointmentStatus.LATE_RESCHEDULING_HOLD,
  AppointmentStatus.RESCHEDULING_IN_PROGRESS,
  AppointmentStatus.LATE_RESCHEDULING_IN_PROGRESS,
  AppointmentStatus.DEPOSITION_HOLD,
  AppointmentStatus.REEVAL_HOLD,
  AppointmentStatus.NO_SHOW,
]);

const DUE_DATE_OFFSETS: Partial<Record<AppointmentType, number>> = {
  AME: 30,
  QME: 30,
  QME_REEVALUATION: 30,
  AME_REEVALUATION: 30,
  SUPPLEMENTAL: 60,
};

/**
 * Calculates the due date for a given appointment based on its type and date,
 * excluding certain appointment statuses for which due date is not applicable.
 *
 * @param appointment - The appointment record containing type, status, and date.
 * @returns A formatted due date string in "MM/dd/yyyy" format, or null if
 *          the due date is not applicable or cannot be calculated.
 */
export const getAppointmentDueDateISO = (
  appointment: AppointmentRecord,
): string | null => {
  const { type, status, date, inquiryDate } = appointment;

  // If status is missing or in the excluded list, due date does not apply
  if (!status || DUE_DATE_EXCLUDED_STATUSES.has(status)) return null;

  // If appointment type is missing, cannot calculate due date
  if (!type) return null;

  // Get number of days to add based on appointment type
  const daysToAdd = DUE_DATE_OFFSETS[type];
  if (daysToAdd == null) return null;

  // For supplemental appointments, use inquiry date; for others, use appointment date
  const baseDate = type === AppointmentType.SUPPLEMENTAL ? inquiryDate : date;

  // If the required date is missing, cannot calculate due date
  if (!baseDate) return null;

  // The repo-wide "date not known" placeholder (inquiry_creator writes it for
  // a dateless inquiry; triage_v2 calls it DATE_SENTINEL). Treated as no base
  // date at all — otherwise a supplemental whose inquiryDate is the sentinel
  // renders a due date in 1970 and reads as maximally overdue. Mirrors
  // `_UNSET_DATE_SENTINEL` in the Python kernel.
  if (baseDate.slice(0, 10) === "1970-01-01") return null;

  // Truncate to the `yyyy-MM-dd` prefix before the arithmetic. Some prod rows
  // carry an AWSDate designator or a full timestamp in `date` / `inquiryDate`
  // ("2024-01-01Z", "2024-01-01T09:30:00Z" — see
  // scripts/dynamo/backfill_awsdate_designators.py). getFutureDateFromDateISO
  // parses by splitting on "-", so those shapes yield NaN and it throws
  // RangeError rather than returning null — inside a cell renderer that blanks
  // the page. Mirrors `_parse_base_date` in the Python kernel, which truncates
  // the same way, so both implementations agree on these rows.
  return getFutureDateFromDateISO(baseDate.slice(0, 10), daysToAdd);
};

export const calculateDueDate = (
  appointment: AppointmentRecord,
): string | null => {
  const dueDateISO = getAppointmentDueDateISO(appointment);
  return dueDateISO ? formatDate(dueDateISO) : null;
};

/**
 * Resolves the report due date for an appointment as an ISO "yyyy-MM-dd"
 * string, preferring an explicitly stored `reportDue` (authoritative override)
 * and otherwise falling back to the type-based calculation. Shared by the
 * due-date column display, the due-date filter, and the color coding so all
 * three agree on a single value.
 */
export const resolveDueDateISO = (
  appointment: AppointmentRecord,
): string | null =>
  appointment.reportDue || getAppointmentDueDateISO(appointment);

/** Preset due-date filter buckets, evaluated relative to today (PT). */
export enum DueDatePreset {
  PAST_DUE = "PAST_DUE",
  DUE_IN_7_DAYS = "DUE_IN_7_DAYS",
  DUE_IN_14_DAYS = "DUE_IN_14_DAYS",
}

export const DUE_DATE_PRESET_LABELS: Record<DueDatePreset, string> = {
  [DueDatePreset.PAST_DUE]: "Past due",
  [DueDatePreset.DUE_IN_7_DAYS]: "Due in 7 days",
  [DueDatePreset.DUE_IN_14_DAYS]: "Due in 14 days",
};

/** True when an ISO "yyyy-MM-dd" due date falls in the given preset bucket. */
export const dueDateISOInPreset = (
  dueDateISO: string,
  preset: DueDatePreset,
): boolean => {
  const today = getTodayDateISOStr();
  switch (preset) {
    case DueDatePreset.PAST_DUE:
      return dueDateISO < today;
    case DueDatePreset.DUE_IN_7_DAYS:
      return (
        dueDateISO >= today && dueDateISO <= getFutureDateFromDateISO(today, 7)
      );
    case DueDatePreset.DUE_IN_14_DAYS:
      return (
        dueDateISO >= today && dueDateISO <= getFutureDateFromDateISO(today, 14)
      );
  }
};

/** True when an ISO due date matches ANY of the selected presets. */
export const dueDateISOMatchesAnyPreset = (
  dueDateISO: string,
  presets: string[],
): boolean =>
  presets.some((preset) =>
    dueDateISOInPreset(dueDateISO, preset as DueDatePreset),
  );

export type DueDateSeverity = "past-due" | "due-soon";

/**
 * Color-coding severity for the report due date: `past-due` (red) when the
 * report is past due or due today, `due-soon` (orange) when due within the next
 * 7 days, and `null` (no color) otherwise. Suppressed once the report is
 * `READY_TO_SUBMIT` — at that point the due date no longer needs chasing.
 */
export const getDueDateSeverity = (
  appointment: AppointmentRecord,
): DueDateSeverity | null => {
  if (appointment.reportStatus === ReportStatus.READY_TO_SUBMIT) return null;

  const dueDateISO = resolveDueDateISO(appointment);
  if (!dueDateISO) return null;

  const today = getTodayDateISOStr();
  if (dueDateISO <= today) return "past-due";
  if (dueDateISO <= getFutureDateFromDateISO(today, 7)) return "due-soon";
  return null;
};

/**
 * Gets the appropriate S3 key for file preview using Document.preview.
 *
 * @param file - The file object with id and original key
 * @returns Promise resolving to the appropriate S3 key for preview
 */
export const getFilePreviewKey = async (file: {
  id: string;
  key: string;
}): Promise<string> => {
  // Check if there's a document preview available
  const documentPreviewKey = await getDocumentPreviewKey(
    file.id,
    amplifyGraphQLClient,
  );

  if (documentPreviewKey) {
    return documentPreviewKey;
  }

  // Use the original key as fallback
  return file.key;
};

/**
 * Return `originalFileName` with its extension swapped to match the extension
 * of `referenceKey` (typically a preview S3 key). Preserves the user-facing
 * filename while keeping the extension aligned with the actual bytes being
 * served (e.g. original `report.docx` + preview `.pdf` → `report.pdf`).
 */
export const withExtensionFrom = (
  originalFileName: string,
  referenceKey: string,
): string => {
  const refBase = referenceKey.split("/").pop() || referenceKey;
  const refDot = refBase.lastIndexOf(".");
  const refExt = refDot >= 0 ? refBase.slice(refDot + 1).toLowerCase() : "";

  const origDot = originalFileName.lastIndexOf(".");
  const origStem =
    origDot >= 0 ? originalFileName.slice(0, origDot) : originalFileName;
  const origExt =
    origDot >= 0 ? originalFileName.slice(origDot + 1).toLowerCase() : "";

  if (!refExt || refExt === origExt) {
    return originalFileName;
  }
  return `${origStem}.${refExt}`;
};

/**
 * Parses US address strings into components using the parse-address library.
 */

const STATE_ABBREVIATIONS = new Set(
  STATE_OPTIONS.map((option) => option.value.toUpperCase()),
);

const STATE_LABEL_TO_ABBREVIATION = STATE_OPTIONS.reduce(
  (acc, option) => {
    acc[option.label.toLowerCase()] = option.value.toUpperCase();
    return acc;
  },
  {} as Record<string, string>,
);

const normalizeStateValue = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const upperCandidate = trimmed.toUpperCase();
  if (STATE_ABBREVIATIONS.has(upperCandidate)) {
    return upperCandidate;
  }

  const mapped = STATE_LABEL_TO_ABBREVIATION[trimmed.toLowerCase()];
  return mapped ?? undefined;
};

const cleanWhitespace = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }

  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized || undefined;
};

export interface ParsedAddressFields {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

type LibraryParsedAddress = NonNullable<
  ReturnType<typeof parseAddress.parseLocation>
>;

const collectUniqueTokens = (
  ...candidates: Array<string | undefined>
): string[] => {
  const seen = new Set<string>();
  const tokens: string[] = [];

  candidates.forEach((candidate) => {
    const normalized = cleanWhitespace(candidate);
    if (!normalized) {
      return;
    }

    const key = normalized.toLowerCase();
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    tokens.push(normalized);
  });

  return tokens;
};

const joinSegments = (...segments: Array<string | undefined>): string => {
  const normalized = segments
    .map((segment) => cleanWhitespace(segment))
    .filter((segment): segment is string => Boolean(segment));

  return normalized.join(" ");
};

const buildCompositeSegment = (
  ...candidates: Array<string | undefined>
): string => collectUniqueTokens(...candidates).join(" ");

const buildStreetLineFromParsedAddress = (
  parsed: LibraryParsedAddress,
  normalizedFallback: string,
): string => {
  const streetCore = joinSegments(
    buildCompositeSegment(parsed.number, parsed.fraction),
    buildCompositeSegment(parsed.prefix, parsed.prefix1, parsed.prefix2),
    buildCompositeSegment(
      parsed.street,
      parsed.street1,
      parsed.street2,
      parsed.street3,
    ),
    buildCompositeSegment(
      parsed.type,
      parsed.type1,
      parsed.type2,
      parsed.type3,
    ),
    buildCompositeSegment(parsed.suffix, parsed.suffix1, parsed.suffix2),
  );

  const unitSegment = joinSegments(
    buildCompositeSegment(
      parsed.sec_unit_type,
      parsed.sec_unit_type_1,
      parsed.sec_unit_type_2,
      parsed.sec_unit_type_3,
    ),
    parsed.sec_unit_num,
  );

  const streetWithUnit = joinSegments(streetCore, unitSegment);
  return streetWithUnit || normalizedFallback;
};

const resolveCity = (parsed: LibraryParsedAddress): string | undefined => {
  return (
    cleanWhitespace(
      parsed.city ?? parsed.city1 ?? parsed.city2 ?? parsed.place ?? undefined,
    ) || undefined
  );
};

const resolveState = (parsed: LibraryParsedAddress): string | undefined => {
  return normalizeStateValue(parsed.state ?? parsed.state1 ?? parsed.state2);
};

const resolveZip = (parsed: LibraryParsedAddress): string | undefined => {
  const baseZip = cleanWhitespace(parsed.zip);
  if (!baseZip) {
    return undefined;
  }

  const plusFour = cleanWhitespace(parsed.plus4);
  return plusFour ? `${baseZip}${plusFour}` : baseZip;
};

const parseWithLibrary = (normalized: string): ParsedAddressFields | null => {
  try {
    const parsed = parseAddress.parseLocation(normalized);

    if (!parsed) {
      return null;
    }

    const street = buildStreetLineFromParsedAddress(parsed, normalized);
    const city = resolveCity(parsed);
    const state = resolveState(parsed);
    const zip = resolveZip(parsed);

    const result: ParsedAddressFields = {};

    if (street) {
      result.street = street;
    }

    if (city) {
      result.city = city;
    }

    if (state) {
      result.state = state;
    }

    if (zip) {
      result.zip = zip;
    }

    return Object.keys(result).length > 0 ? result : null;
  } catch {
    return null;
  }
};

/**
 * Attempts to parse a single-line address like:
 * "PO Box 968070, Schaumburg, IL 60196"
 * into street/city/state/zip components.
 */
export const parseSingleLineAddress = (
  rawAddress?: string | null,
): ParsedAddressFields => {
  if (!rawAddress) {
    return {};
  }

  const normalized = cleanWhitespace(rawAddress);

  if (!normalized) {
    return {};
  }

  const parsed = parseWithLibrary(normalized);
  return parsed ?? {};
};

/**
 * Check if a file belongs to the current appointment by comparing its S3 key prefix
 * with the appointment's S3 file key.
 */
export const isCurrentAppointmentFile = (
  file:
    | {
        key?: string | null;
        type?: FileType | null;
        sourceAppointmentID?: string | null;
      }
    | null
    | undefined,
  appointment: { id: string; s3FileKey?: string | null } | null | undefined,
): boolean => {
  if (!file?.key || !appointment?.s3FileKey) {
    return false;
  }

  // For Notice 110 forms, check sourceAppointmentID if available, otherwise fall back to S3 prefix
  if (file.type === FileType.QME_NOTIFICATION_FORM) {
    if (
      file.sourceAppointmentID !== null &&
      file.sourceAppointmentID !== undefined
    ) {
      return file.sourceAppointmentID === appointment.id;
    }
    // Fall back to S3 prefix logic for backward compatibility (pre-sourceAppointmentID files)
    return file.key.startsWith(appointment.s3FileKey);
  }

  // For other files, use existing S3 prefix logic
  return file.key.startsWith(appointment.s3FileKey);
};

export const getAppointmentsViewFromPath = (path: string): AppointmentsView => {
  if (path.includes("/appointments")) return AppointmentsView.APPOINTMENTS;
  if (path.includes("/active-visits")) return AppointmentsView.ACTIVE_VISITS;
  if (path.includes("/active-cases")) return AppointmentsView.ACTIVE_CASES;
  if (path.includes("/past-cases")) return AppointmentsView.PAST_CASES;
  if (path.includes("/upcoming-appointments"))
    return AppointmentsView.UPCOMING_APPOINTMENTS;
  if (path.includes("/bookings")) return AppointmentsView.UPCOMING_APPOINTMENTS;
  if (
    path.includes("/in-person-evaluations") ||
    path.includes("/previsit-appointments")
  )
    return AppointmentsView.PREVISIT_APPOINTMENTS;
  if (path.includes("/billing-and-collection"))
    return AppointmentsView.BILLING_AND_COLLECTION;
  if (path.includes("/today-visits")) return AppointmentsView.TODAY_VISITS;
  if (path.includes("/depositions")) return AppointmentsView.DEPOSITIONS;
  if (path.includes("/supplementals")) return AppointmentsView.SUPPLEMENTALS;
  return AppointmentsView.APPOINTMENTS; // Default view
};

/**
 * Consolidates medical records page counts from multiple sources with priority logic.
 *
 * This function determines which page count to use for medical records processing by prioritizing
 * attorney page declarations over the actual processed page count from PDF metadata.
 *
 * IMPORTANT: We must distinguish between null/undefined and 0:
 * - null/undefined = field not set/not provided → ignore this declaration, use other sources
 * - 0 = field explicitly set to zero → use this declared value (even if 0)
 *
 * Only use attorney declarations if they are explicitly provided. Otherwise, use the
 * fallback calculated page count from PDF processing.
 *
 * Logic:
 * - If either DA or AA page declaration is explicitly provided (including 0): Use declared logic
 *   - If only one declaration is provided: Use that declaration (even if 0)
 *   - If both declarations are provided: Sum them together
 * - If neither declaration is provided (both null/undefined): Fall back to processed pages
 *
 * @param defenseAttorneyPages - Defense attorney page declaration (null/undefined = not provided, 0+ = explicitly declared)
 * @param applicantAttorneyPages - Applicant attorney page declaration (null/undefined = not provided, 0+ = explicitly declared)
 * @param fallbackProcessedPages - Fallback page count from PDF processing (default: 0)
 * @returns The consolidated page count to use for medical records processing
 *
 * @example
 * consolidateMedicalRecordsPageCounts(5, 3, 100) // → 8 (DA 5 + AA 3)
 * consolidateMedicalRecordsPageCounts(0, 5, 100) // → 5 (DA explicitly 0 + AA 5)
 * consolidateMedicalRecordsPageCounts(7, null, 100) // → 7 (only DA provided)
 * consolidateMedicalRecordsPageCounts(null, null, 100) // → 100 (no declarations, use fallback)
 */
export const consolidateMedicalRecordsPageCounts = (
  defenseAttorneyPages: number | null | undefined,
  applicantAttorneyPages: number | null | undefined,
  fallbackProcessedPages = 0,
): number => {
  // Check if either attorney has explicitly provided a page declaration
  // Note: We check for !== null && !== undefined to distinguish between 0 (explicit) and null/undefined (not provided)
  if (defenseAttorneyPages !== null && defenseAttorneyPages !== undefined) {
    if (
      applicantAttorneyPages !== null &&
      applicantAttorneyPages !== undefined
    ) {
      // Both attorneys provided declarations - sum them together
      return defenseAttorneyPages + applicantAttorneyPages;
    } else {
      // Only DA provided a declaration - use DA's value (even if 0)
      return defenseAttorneyPages;
    }
  } else if (
    applicantAttorneyPages !== null &&
    applicantAttorneyPages !== undefined
  ) {
    // Only AA provided a declaration - use AA's value (even if 0)
    return applicantAttorneyPages;
  } else {
    // Neither attorney provided a declaration (both null/undefined) - use fallback
    return fallbackProcessedPages;
  }
};

// Utility: Generate select options for enums
export interface SelectOption<T> {
  value: T;
  label: string;
}

export const getEnumOptions = <T extends string>(
  enumObj: Record<string, T>,
  labelFn: (v: T) => string,
): SelectOption<T>[] =>
  Object.values(enumObj).map((value) => ({ value, label: labelFn(value) }));

// Utility: Generate duration options
export const getDurationOptions = (): SelectOption<number>[] => [
  { value: 30, label: "30 minutes" },
  { value: 45, label: "45 minutes" },
  { value: 60, label: "60 minutes" },
  { value: 90, label: "90 minutes" },
];

/** Human-readable display names for Region enum values */
export const REGION_DISPLAY_NAMES: Record<Region, string> = {
  [Region.SF_BAY_AREA]: "SF Bay Area",
  [Region.LOS_ANGELES]: "Los Angeles",
  [Region.SAN_DIEGO]: "San Diego",
};

// Utility: Generate location options
export const getLocationOptions = (
  locations: { id: string; name?: string | null }[],
): SelectOption<string>[] =>
  locations.map((loc) => ({ value: loc.id, label: loc.name ?? "" }));

/**
 * Creates a custom filter function for location dropdowns that matches
 * against both location name and address fields (street, city, state, zip).
 * This allows users to search by typing any part of the address.
 */
export const createLocationFilterFn = (
  locations: Location[],
): ((input: string, optionValue: string) => boolean) => {
  // Build a lookup map for efficient filtering
  const locationMap = new Map<string, Location>();
  locations.forEach((loc) => locationMap.set(loc.id, loc));

  return (input: string, optionValue: string): boolean => {
    const lowerInput = input.toLowerCase().trim();
    if (!lowerInput) return true;

    const location = locationMap.get(optionValue);
    if (!location) return false;

    // Match against location name
    if (location.name?.toLowerCase().includes(lowerInput)) {
      return true;
    }

    // Match against address fields
    if (location.address) {
      const { street, city, state, zip } = location.address;
      const addressParts = [street, city, state, zip]
        .filter(Boolean)
        .map((part) => part?.toLowerCase());

      // Check if input matches any address part
      if (addressParts.some((part) => part?.includes(lowerInput))) {
        return true;
      }

      // Also check full address string for multi-word searches
      const fullAddress = addressParts.join(" ");
      if (fullAddress.includes(lowerInput)) {
        return true;
      }
    }

    return false;
  };
};

// Utility: Generate physician location options
export const getPhysicianLocationOptions = ({
  allLocations,
  allDoctors,
  preferredPhysician,
  preferredLocation,
}: {
  allLocations: Location[];
  allDoctors: Doctor[];
  preferredPhysician: string | null | undefined;
  preferredLocation: string | null | undefined;
}): { value: string; label: string }[] => {
  // Find selected doctor
  const selectedDoctor = preferredPhysician
    ? allDoctors.find(({ id }) => id === preferredPhysician)
    : undefined;
  // Extract location ids from selectedDoctor.locations.items
  const doctorLocationIds: string[] =
    selectedDoctor &&
    selectedDoctor.locations &&
    Array.isArray(selectedDoctor.locations.items)
      ? ((
          selectedDoctor.locations.items as Array<{
            location?: { id?: string | null };
          }> | null
        )
          ?.map((item) => item?.location?.id)
          .filter((id): id is string => !!id) ?? [])
      : [];
  let filteredLocations: (Location & { name: string })[] = [];
  if (doctorLocationIds.length > 0) {
    filteredLocations = allLocations.filter(
      (location): location is Location & { name: string } =>
        !!location.name &&
        location.name.trim() !== "" &&
        location.status !== LocationStatus.INACTIVE &&
        doctorLocationIds.includes(location.id),
    );
    // If doctor has no valid locations after filtering (e.g. data mismatch), fallback to all
    if (filteredLocations.length === 0) {
      filteredLocations = allLocations.filter(
        (location): location is Location & { name: string } =>
          !!location.name &&
          location.name.trim() !== "" &&
          location.status !== LocationStatus.INACTIVE,
      );
    }
  } else {
    // If doctor has no locations, show all
    filteredLocations = allLocations.filter(
      (location): location is Location & { name: string } =>
        !!location.name &&
        location.name.trim() !== "" &&
        location.status !== LocationStatus.INACTIVE,
    );
  }
  // Ensure selected location is present in options even if not in filteredLocations
  if (
    preferredLocation &&
    !filteredLocations.some((loc) => loc.id === preferredLocation)
  ) {
    // Try to find the location in allLocations
    const selectedLocation = allLocations.find(
      (loc) => loc.id === preferredLocation,
    );
    if (selectedLocation && selectedLocation.name) {
      // Keep a now-inactive selected location visible (so editing doesn't
      // silently drop it), but flag it — mirrors the MA inactive handling.
      const label =
        selectedLocation.status === LocationStatus.INACTIVE
          ? `${selectedLocation.name} (inactive)`
          : selectedLocation.name;
      filteredLocations.push({
        ...selectedLocation,
        name: label,
      } as Location & { name: string });
    } else {
      // If not found, add a placeholder option
      filteredLocations.push({
        id: preferredLocation,
        name: preferredLocation,
      } as Location & { name: string });
    }
  }
  return filteredLocations
    .map((location) => ({ value: location.id, label: location.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const getReevaluationTypeByFileKey = (
  s3FileKey?: string | null,
): AppointmentType | null => {
  if (!s3FileKey) return null;
  if (s3FileKey.startsWith("QME")) return AppointmentType.QME_REEVALUATION;
  if (s3FileKey.startsWith("AME")) return AppointmentType.AME_REEVALUATION;
  if (s3FileKey.startsWith("SIBTF")) return AppointmentType.SIBTF_REEVALUATION;
  // Check IME_ADR before IME since IME_ADR starts with "IME"
  if (s3FileKey.startsWith("IME_ADR"))
    return AppointmentType.IME_ADR_REEVALUATION;
  if (s3FileKey.startsWith("IME")) return AppointmentType.IME_REEVALUATION;
  return null;
};

/**
 * Generates a dynamic visit form link with the appointment ID as a parameter.
 * @param appointmentId - The ID of the appointment
 * @param language - The language of the visit form. Only supports "en" and "es" for now (default: "en")
 * @returns The visit form link
 */
export const getVisitFormLink = (
  appointmentId: string,
  language: "en" | "es" = "en",
) => {
  const baseUrl =
    language === "es" ? VISIT_FORM_BASE_URL_SPANISH : VISIT_FORM_BASE_URL;
  return `${baseUrl}?uniqueid=${appointmentId}`;
};

/**
 * Calculates BMI (Body Mass Index) from weight in pounds and height in feet and inches.
 * Formula: BMI = weight (lbs) / (height (inches)^2) * 703
 * @param weight - Weight in pounds
 * @param heightFeet - Height in feet
 * @param heightInches - Height in inches
 * @returns BMI value rounded to 1 decimal place as a string, or empty string if values are invalid
 */
export const calculateBMI = (
  weight: number | null,
  heightFeet: number | null,
  heightInches: number | null,
): string => {
  if (
    weight !== null &&
    weight > 0 &&
    (heightFeet !== null || heightInches !== null)
  ) {
    const totalInches = (heightFeet || 0) * 12 + (heightInches || 0);
    if (totalInches > 0) {
      const bmi = (weight / (totalInches * totalInches)) * 703;
      return bmi.toFixed(1);
    }
  }
  return "";
};

/**
 * Checks if an uploaded file has an associated audio transcript preview available.
 * For DICTATION type files, this function queries the Audio table by uploadedFileID
 * and returns the transcriptPreviewS3Key if available.
 *
 * @param uploadedFileId - The ID of the uploaded file
 * @param fileType - The type of the uploaded file
 * @param apolloClient - GraphQL client instance for making queries
 * @returns Promise resolving to the transcript preview S3 key if available, null otherwise
 */
export const getAudioTranscriptPreviewKey = async (
  uploadedFileId: string,
  fileType: FileType,
  apolloClient: V6Client,
): Promise<string | null> => {
  // Only check for transcript preview if the file is a DICTATION
  if (fileType !== FileType.DICTATION) {
    return null;
  }

  try {
    const variables: AudioByUploadedFileIDQueryVariables = {
      uploadedFileID: uploadedFileId,
      limit: 1,
    };

    const result = await apolloClient.graphql({
      query: audioByUploadedFileIDWithTranscriptPreview,
      variables,
    });

    const audioItems = result.data?.audioByUploadedFileID?.items;
    if (audioItems && audioItems.length > 0) {
      const audio = audioItems[0];
      // Return the S3 key from the transcriptPreview S3Blob
      return audio?.transcriptPreview?.key || null;
    }

    return null;
  } catch (error) {
    console.error("Error fetching audio transcript preview:", error);
    return null;
  }
};

/**
 * Checks if an uploaded file has an associated document preview available.
 * This function queries the Document table by uploadedFileID using the byUploadedFile index
 * and returns the preview S3 key if available.
 *
 * @param uploadedFileId - The ID of the uploaded file
 * @param apolloClient - GraphQL client instance for making queries
 * @returns Promise resolving to the document preview S3 key if available, null otherwise
 */
export const getDocumentPreviewKey = async (
  uploadedFileId: string,
  apolloClient: V6Client,
): Promise<string | null> => {
  try {
    const variables: DocumentsByUploadedFileIDQueryVariables = {
      uploadedFileID: uploadedFileId,
      limit: 1,
    };

    const result = await apolloClient.graphql({
      query: documentsByUploadedFileIDWithPreview,
      variables,
    });

    const documentItems = result.data?.documentsByUploadedFileID?.items;
    if (documentItems && documentItems.length > 0) {
      const document = documentItems[0];
      // Return the S3 key from the preview S3Blob
      return document?.preview?.key || null;
    }

    return null;
  } catch (error) {
    console.error("Error fetching document preview:", error);
    return null;
  }
};

/**
 * Gets the appropriate preview key and filename for a file, using Document.preview first,
 * then transcript preview for DICTATION files. Falls back to original file if no preview available.
 *
 * @param file - The file display data
 * @param apolloClient - GraphQL client instance for making queries
 * @returns Promise resolving to preview key and filename
 */
export const getFilePreviewDetails = async (file: {
  id: string;
  fileType: FileType;
  key: string;
  fileName: string;
}): Promise<{ previewKey: string; previewFileName: string }> => {
  // For DICTATION files, check if there's a transcript preview available
  if (file.fileType === FileType.DICTATION) {
    const transcriptPreviewKey = await getAudioTranscriptPreviewKey(
      file.id,
      file.fileType,
      amplifyGraphQLClient,
    );

    if (transcriptPreviewKey) {
      // Use the transcript preview; show the original filename but with the
      // transcript's extension so the browser picks the right MIME type.
      return {
        previewKey: transcriptPreviewKey,
        previewFileName: withExtensionFrom(file.fileName, transcriptPreviewKey),
      };
    }
  }

  // First, check if there's a document preview available for this uploaded file
  const documentPreviewKey = await getDocumentPreviewKey(
    file.id,
    amplifyGraphQLClient,
  );

  if (documentPreviewKey) {
    return {
      previewKey: documentPreviewKey,
      previewFileName: withExtensionFrom(file.fileName, documentPreviewKey),
    };
  }

  // No preview available - fall back to original file
  return {
    previewKey: file.key,
    previewFileName: file.fileName,
  };
};
